import * as vp from 'vopay';

import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useI18nLabels } from '../hooks/useI18nLabels';
import styles from './ScheduledTransactionsForm.module.scss';

import { Button } from '#shared/components/UI';

const { Select, TextField, DateRangePicker } = vp;

type ScheduledTransactionSearchParams = {
  scheduleType: string;
  transactionType: string;
  frequency: vp.NameOfFrequency | 'all' | '';
  status: string;
  searchTerm: string;
  timeRange: [vp.Timestamp, vp.Timestamp];
};

type SearchFormProps = {
  timeRange?: [vp.Timestamp, vp.Timestamp];
  scheduleType: string;
  transactionType: string;
  frequency: vp.NameOfFrequency | 'all' | '';
  status: string;
  searchTerm: string;
  initialTimeRange: [vp.Timestamp, vp.Timestamp];
  hasInvalidEndDate?: boolean;
  shouldShowEndDateError?: boolean;
  setShouldShowEndDateError?: (event: boolean) => void;
  search: (searchParams: ScheduledTransactionSearchParams) => void;
};

type FC = FunctionComponent<SearchFormProps>;

const ScheduledTransactionsForm: FC = (props: SearchFormProps) => {
  const {
    scheduleType,
    transactionType,
    frequency,
    status,
    searchTerm,
    initialTimeRange,
    hasInvalidEndDate,
    shouldShowEndDateError,
    setShouldShowEndDateError,
    search,
  } = props;

  const [currentScheduleType, setCurrentScheduleType] = useState<string>(scheduleType);
  const [currentTransactionType, setCurrentTransactionType] = useState<string>(transactionType);
  const [currentFrequency, setCurrentFrequency] = useState<vp.NameOfFrequency | 'all' | ''>(frequency);
  const [currentStatus, setCurrentStatus] = useState<string>(status);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>(searchTerm);
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp]>(initialTimeRange);
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ScheduledTransactions.List.SearchForm' });
  const { t: tGlobalFrequency } = useTranslation('translations', { keyPrefix: 'Shared.FrequencyOptions' });
  const { frequencyLabelMapper } = useI18nLabels();

  const scheduleTypeOption = [
    { value: 'all', label: t('ScheduleType.Options.all', { defaultValue: 'All' }) },
    { value: 'single', label: t('ScheduleType.Options.single', { defaultValue: 'Single' }) },
    { value: 'recurring', label: t('ScheduleType.Options.recurring', { defaultValue: 'Recurring' }) },
  ];

  const transactionTypeOption = [
    { value: 'all', label: t('TransactionType.Options.all', { defaultValue: 'All' }) },
    { value: 'fund', label: t('TransactionType.Options.fund', { defaultValue: 'Fund' }) },
    { value: 'withdraw', label: t('TransactionType.Options.withdraw', { defaultValue: 'Withdraw' }) },
  ];

  const frequencyValues: vp.NameOfFrequency[] = [
    'daily',
    'weekly',
    'bi-weekly',
    'monthly',
    'semi-monthly',
    'quarterly',
    'semi-annually',
    'annually',
    'weekdays',
    'custom',
  ];

  const frequencyOptions = [
    { value: 'all', label: tGlobalFrequency('all', { defaultValue: 'All' }) },
    ...frequencyValues.map((value) => ({ value, label: frequencyLabelMapper(value) })),
  ];

  // GET /scheduled-payment validates Status against 'in progress' | 'cancelled' | 'completed'
  const statusOptions = [
    { value: 'all', label: t('Status.Options.all', { defaultValue: 'All' }) },
    { value: 'cancelled', label: t('Status.Options.cancelled', { defaultValue: 'Cancelled' }) },
    { value: 'completed', label: t('Status.Options.completed', { defaultValue: 'Completed' }) },
    { value: 'in progress', label: t('Status.Options.in progress', { defaultValue: 'In Progress' }) },
  ];

  return (
    <div className={styles.formContainer}>
      <div className={styles.dateRange}>
        <DateRangePicker
          onChange={(range) => {
            setTimeRange(range);
          }}
          initialTime={initialTimeRange}
          triggerVariant="outlineShadow"
          hasInvalidEndDate={hasInvalidEndDate}
          shouldShowEndDateError={shouldShowEndDateError}
          setShouldShowEndDateError={setShouldShowEndDateError}
        />
      </div>

      <div className={styles.field}>
        <Select
          version="2"
          placeholder={t('ScheduleType.placeholder', { defaultValue: 'Schedule Type' })}
          options={scheduleTypeOption}
          name="scheduleType"
          value={currentScheduleType}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentScheduleType(e.target.value);
          }}
        />
      </div>

      <div className={styles.field}>
        <Select
          version="2"
          placeholder={t('TransactionType.placeholder', { defaultValue: 'Transaction Type' })}
          options={transactionTypeOption}
          name="transactionType"
          value={currentTransactionType}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentTransactionType(e.target.value);
          }}
        />
      </div>

      <div className={styles.field}>
        <Select
          version="2"
          placeholder={t('Frequency.placeholder', { defaultValue: 'Frequency' })}
          options={frequencyOptions}
          sort={false}
          name="frequency"
          value={currentFrequency}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentFrequency(e.target.value as vp.NameOfFrequency | 'all');
          }}
        />
      </div>

      <div className={styles.field}>
        <Select
          version="2"
          placeholder={t('Status.placeholder', { defaultValue: 'Status' })}
          options={statusOptions}
          name="status"
          value={currentStatus}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentStatus(e.target.value);
          }}
        />
      </div>

      <div className={styles.field}>
        <TextField
          key="SearchKeyword"
          name="SearchKeyword"
          placeholder={t('keywordPlaceholder', { defaultValue: 'Keyword' })}
          value={currentSearchTerm}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentSearchTerm(e.target.value);
          }}
          variant="outlineShadow"
        />
      </div>

      <Button
        id="search-scheduled-transactions"
        size="small"
        colorScheme="primary"
        onClick={() => {
          search({
            scheduleType: currentScheduleType,
            transactionType: currentTransactionType,
            searchTerm: currentSearchTerm,
            frequency: currentFrequency,
            status: currentStatus,
            timeRange,
          });
        }}
      >
        {t('searchButtonLabel', { defaultValue: 'Search' })}
      </Button>
    </div>
  );
};

export default ScheduledTransactionsForm;
